import React, { useState, useEffect, useRef } from 'react';
import ProgressBar from 'dashboards/lite/ProgressBar';
import showAlert from "utils/showAlert";

const LiteRefresh = ({ currentDashboard, accountId, alreadyPulling, refreshPreference, icon, companyName, modalButtonRef, setShowModalMenu, showModalMenu, modalMenuRef }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(alreadyPulling > 0 || false);
  const [progress, setProgress] = useState(0);
  const loadingGif = require("images/generate-spinner.gif");
  const authenticityToken = document.querySelector('meta[name="csrf-token"]').content;
  const [totalQueries, setTotalQueries] = useState(alreadyPulling);
  const [dataPullComplete, setDataPullComplete] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(refreshPreference ? refreshPreference : false);

  let dotsHorizontal = require("images/lite/icons/dots-horizontal.png");
  dotsHorizontal = <img className="refresh-icon" src={`/datapulse${dotsHorizontal}`} />;
  let refreshIcon = require("images/icons/refresh-icon.png");
  refreshIcon = <img className="refresh-page-icon" src={`/datapulse${refreshIcon}`} />;

  const menuRef = useRef(null); // Ref for the menu
  const buttonRef = useRef(null); // Ref for the button

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  useEffect(() => {
    const intervalId = loading ? setInterval(() => {
      fetchRefreshStatus(totalQueries);
    }, 7000) : null;

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [loading, totalQueries]); // Depend on `loading` to start or stop the interval

  useEffect(() => {
    if (dataPullComplete && autoRefresh) {
      window.location.reload();
    }
  }, [dataPullComplete, autoRefresh]); // Reacts to changes in dataPullComplete and autoRefresh

  const fetchRefreshStatus = async (totalQueriesCurrent) => {
    const params = new URLSearchParams({
      dashboard: currentDashboard,
      accountId
    });
    
    try {
      const response = await fetch(`/datapulse/lite/refresh-status?${params.toString()}`, {
        method: 'POST', // Assuming GET is the method to fetch status
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': authenticityToken,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Refresh status fetched successfully', data);
        updateProgress(data, totalQueriesCurrent);
      } else {
        console.error('Failed to fetch refresh status');
      }
    } catch (error) {
      console.error('Network error:', error);
    }

  };

  const updateRefreshPreference = async (autoRefresh) => {
    const params = new URLSearchParams({
      default_refresh: autoRefresh == true ? 1 : 0
    });
    try {
      const response = await fetch(`/datapulse/update-default-refresh?${params.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': authenticityToken,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Refresh preference updated successfully', data);
      } else {
        console.error('Failed to update refresh preference');
      }
    } catch (error) {
      console.error('Network error:', error);
    }

  }

  const updateProgress = (data, totalQueriesCurrent) => {
    const remainingQueries = data.total_jobs; // total_jobs updates with each fetch
    if (remainingQueries === 0) {
      setLoading(false);
      setDataPullComplete(true);
      document.querySelector("#most-recent-refresh").innerText = `Last refresh: ${new Date().toLocaleString("en-US", { hour: "numeric", minute: "numeric", hour12: true, month: "numeric", day: "numeric", year: "numeric" })}`;
    }
    const completedQueries = totalQueriesCurrent - remainingQueries;
    console.log("totalQueries", totalQueriesCurrent, "remainingQueries", remainingQueries, "completedQueries", completedQueries, "data", data)
    setProgress((completedQueries / ((totalQueries && (totalQueries > 0)) ? totalQueries : 1)) * 100);
  };


  const handleRefresh = async (timePeriod) => {
    setLoading(true);
    setShowMenu(false);
    setDataPullComplete(false);
    setProgress(0);
    try {
      const params = new URLSearchParams({
        dashboard: currentDashboard,
        accountId: accountId, 
        timePeriod: timePeriod,
      });
      
      // Goes to LiteController#refresh
      const response = await fetch(`/datapulse/lite/refresh?${params.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': authenticityToken,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success == true) {
          setTotalQueries(data.total_jobs);
          console.log('Dashboard refresh initiated successfully', `Total jobs: ${data.total_jobs}`);
        } else {
          // Handle errors
          showAlert(`Dashboard refresh failed - ${data.error}`, 'alert-danger');
          console.error(`Dashboard refresh failed - ${data.error}`);
          setLoading(false);
        }
      } else {
        // Handle errors
        showAlert('Dashboard refresh failed - Server Error', 'alert-danger');
        console.error('Dashboard refresh failed - Server Error');
        setLoading(false);
      }
    } catch (error) {
      // Handle network errors
      showAlert('Dashboard refresh failed - Network Error', 'alert-danger');
      console.error('Network error:', error);
    } finally {
      // No
    }
  };

  return (
    <>
      {(loading || dataPullComplete) && (
        <div className="progress-bar-container">
          <div className="pb-right">
            {dataPullComplete ? (
              <>
                <div className="pb-top">
                  <div><p>Data refreshed!</p></div>
                  <button className="refresh-page lite-btn btn-default" onClick={() => window.location.reload()}>
                    {refreshIcon}
                    Refresh page
                  </button>
                </div>
                <div className="pb-middle">
                  <p>Reload the page to see your new data</p>
                </div>
              </>
            ) : (
              <>
                <div className="pb-top">
                  <div><p>Refreshing data...</p></div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="checkbox"
                      style={{ marginBottom: "2px" }}
                      id="default-refresh"
                      label="Refresh when complete"
                      name="default-refresh"
                      checked={autoRefresh}
                      onChange={(e) => {
                        setAutoRefresh(!autoRefresh);
                        updateRefreshPreference(!autoRefresh);
                      }}
                    />
                    <label htmlFor="default-refresh" style={{ marginLeft: "7px" }}>
                      Refresh when complete
                    </label>
                  </div>
                </div>
                <div className="pb-middle">
                  <p>You can continue browsing DataPulse</p>
                </div>
              </>
            )}
            <ProgressBar progress={progress.toFixed(0)} />
          </div>
        </div>
      )}         
      <div style={ currentDashboard == "all_dashboards" ? {marginRight: "auto",  position: "relative"} : {position: "relative"}}>
        {
          currentDashboard == "all_dashboards" ? (
            <>
              <button  
                ref={modalButtonRef}
                onClick={() => setShowModalMenu(!showModalMenu)}
                disabled={alreadyPulling.total_jobs > 0}
                type="button" className={`button b-type-primary b-size-normal b-color-green ${loading ? 'loading' : ''}`}
               >
                {icon} Repull<span className="desktop-only"> all data for {companyName}</span>
              </button> 
              {showModalMenu && (
                <div ref={modalMenuRef} className="refresh-menu-modal" style={{ position: 'absolute', marginTop: '10px' }}>
                  <div className="refresh-item" onClick={() => {handleRefresh('currentMonth'), window.location.reload()}}>Refresh Current Month</div>
                  <div className="refresh-item" onClick={() => {handleRefresh('last60Days'); window.location.reload()}}>Last 60 Days</div>
                  <div className="refresh-item" onClick={() => {handleRefresh('wholeDashboard'); window.location.reload()}}>Refresh Whole Dashboard</div>
                </div>
              )}
            </>
          ) : (
            <>
              <button
                ref={buttonRef}
                className={`lite-refresh-button ${loading ? 'loading' : ''}`}
                onClick={() => setShowMenu(!showMenu)}
                disabled={loading}
              >
                <div className="lite-flexbox">
                  <p>Refresh<span className="desktop-only"> data</span></p>
                  {dotsHorizontal}
                </div>
              </button>
              {showMenu && (
                <div ref={menuRef} className="refresh-menu" style={{ position: 'absolute', marginTop: '10px' }}>
                  <div className="refresh-item" onClick={() => handleRefresh('currentMonth')}>Refresh Current Month</div>
                  <div className="refresh-item" onClick={() => handleRefresh('last60Days')}>Last 60 Days</div>
                  <div className="refresh-item" onClick={() => handleRefresh('wholeDashboard')}>Refresh Whole Dashboard</div>
                </div>
              )}
            </>
          )    
        }
      </div>
    </>
  );
};

export default LiteRefresh;
